import * as React from "react";
import { createPortal } from "react-dom";
import { X, GripVertical } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const floatingPanelVariants = cva(
  "fixed z-50 flex flex-col bg-background shadow-2xl transition-all duration-300 ease-out " +
    "data-[state=open]:animate-in data-[state=closed]:animate-out " +
    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
    "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
  {
    variants: {
      position: {
        bottom: "bottom-0 left-0 right-0 rounded-t-[10px] sm:rounded-t-2xl",
        "bottom-left":
          "max-sm:inset-x-0 max-sm:bottom-0 max-sm:rounded-t-2xl sm:bottom-4 sm:left-4 sm:rounded-2xl",
        "bottom-right":
          "max-sm:inset-x-0 max-sm:bottom-0 max-sm:rounded-t-2xl sm:bottom-4 sm:right-4 sm:rounded-2xl",
        left: "left-0 top-0 bottom-0 h-full rounded-r-[10px] sm:rounded-r-2xl",
        right: "right-0 top-0 bottom-0 h-full rounded-l-[10px] sm:rounded-l-2xl",
      },
      size: {
        sm: "w-full sm:w-[320px] max-h-[50vh]",
        md: "w-full sm:w-[400px] max-h-[65vh]",
        lg: "w-full sm:w-[480px] max-h-[80vh]",
        xl: "w-full sm:w-[560px] max-h-[90vh]",
        full: "w-full h-full sm:max-w-none sm:max-h-none",
      },
      variant: {
        panel: "border border-stone-200 dark:border-stone-700",
        floating: "border border-stone-200 dark:border-stone-700 shadow-2xl shadow-stone-900/15",
        card: "border border-stone-200 dark:border-stone-700 shadow-lg",
      },
    },
    compoundVariants: [
      {
        position: "bottom",
        size: "sm",
        className: "sm:max-w-[320px]",
      },
      {
        position: "bottom",
        size: "md",
        className: "sm:max-w-[400px]",
      },
      {
        position: "bottom",
        size: "lg",
        className: "sm:max-w-[480px]",
      },
      {
        position: "bottom",
        size: "xl",
        className: "sm:max-w-[560px]",
      },
    ],
    defaultVariants: {
      position: "bottom",
      size: "lg",
      variant: "floating",
    },
  },
);

const floatingPanelOverlayVariants = cva(
  "fixed inset-0 z-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  {
    variants: {
      show: {
        true: "bg-black/20",
        false: "bg-transparent pointer-events-none",
      },
    },
    defaultVariants: {
      show: false,
    },
  },
);

interface FloatingPanelProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof floatingPanelVariants> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portal?: boolean;
  showOverlay?: boolean;
  showHandle?: boolean;
  showCloseButton?: boolean;
  snapPoints?: number[];
  defaultSnapPoint?: number;
  keyboardNavigation?: boolean;
  onSnapPointChange?: (point: number) => void;
  contentClassName?: string;
  children: React.ReactNode;
}

const FloatingPanel = React.forwardRef<HTMLDivElement, FloatingPanelProps>(
  (
    {
      open,
      onOpenChange,
      portal = true,
      showOverlay = false,
      showHandle = true,
      showCloseButton = true,
      snapPoints,
      defaultSnapPoint,
      keyboardNavigation = false,
      onSnapPointChange,
      contentClassName,
      position = "bottom",
      size = "lg",
      variant = "floating",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [mounted, setMounted] = React.useState(false);
    const [currentSnap, setCurrentSnap] = React.useState(defaultSnapPoint ?? 1);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const dragStartRef = React.useRef<{ y: number; height: number } | null>(null);

    React.useEffect(() => {
      if (open) {
        setMounted(true);
        requestAnimationFrame(() => {
          if (panelRef.current) {
            panelRef.current.style.setProperty("--panel-height", `${panelRef.current.scrollHeight}px`);
          }
        });
      } else {
        const timer = setTimeout(() => setMounted(false), 300);
        return () => clearTimeout(timer);
      }
    }, [open]);

    React.useEffect(() => {
      if (!open || !keyboardNavigation) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onOpenChange(false);
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          const event = new CustomEvent("floating-panel:navigate", { detail: { direction: "prev" } });
          window.dispatchEvent(event);
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          const event = new CustomEvent("floating-panel:navigate", { detail: { direction: "next" } });
          window.dispatchEvent(event);
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, keyboardNavigation, onOpenChange]);

    const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
      if (!snapPoints || snapPoints.length === 0) return;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const height = panelRef.current?.offsetHeight ?? 0;
      dragStartRef.current = { y: clientY, height };
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);
      document.addEventListener("touchmove", handleDragMove, { passive: true });
      document.addEventListener("touchend", handleDragEnd);
    };

    const handleDragMove = (e: MouseEvent | TouchEvent) => {
      if (!dragStartRef.current || !panelRef.current) return;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const deltaY = dragStartRef.current.y - clientY;
      const newHeight = Math.max(
        snapPoints[0] * window.innerHeight,
        Math.min(dragStartRef.current.height + deltaY, window.innerHeight * 0.95),
      );
      panelRef.current.style.height = `${newHeight}px`;
    };

    const handleDragEnd = () => {
      if (!dragStartRef.current || !panelRef.current || !snapPoints) return;
      const height = panelRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      const ratio = height / viewportHeight;

      let closestSnap = snapPoints[0];
      let minDiff = Infinity;
      for (const point of snapPoints) {
        const diff = Math.abs(ratio - point);
        if (diff < minDiff) {
          minDiff = diff;
          closestSnap = point;
        }
      }

      const snapIndex = snapPoints.indexOf(closestSnap);
      setCurrentSnap(snapIndex);
      panelRef.current.style.height = `${closestSnap * viewportHeight}px`;
      onSnapPointChange?.(closestSnap);
      dragStartRef.current = null;

      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDragMove);
      document.removeEventListener("touchend", handleDragEnd);
    };

    if (!mounted && !open) return null;

    const content = (
      <>
        {showOverlay && (
          <div
            className={cn(floatingPanelOverlayVariants({ show: open }))}
            onClick={() => onOpenChange(false)}
            aria-hidden="true"
          />
        )}
        <div
          ref={panelRef}
          className={cn(
            floatingPanelVariants({ position, size, variant }),
            "slateCardClass",
            "overflow-hidden",
            className,
          )}
          style={{
            transform: open ? "translateY(0)" : "translateY(100%)",
            opacity: open ? 1 : 0,
            height: snapPoints ? undefined : "auto",
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          {...props}
        >
          {(showHandle || showCloseButton) && (
            <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
              {showHandle && snapPoints && snapPoints.length > 1 && (
                <div
                  className="mx-auto h-1.5 w-8 rounded-full bg-muted"
                  aria-label="Drag to resize"
                />
              )}
              {showCloseButton && (
                <button
                  onClick={() => onOpenChange(false)}
                  className={cn(
                    "flex-shrink-0 rounded-sm opacity-70 ring-offset-background transition-opacity",
                    "hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    "disabled:pointer-events-none",
                    "actionPillClass",
                  )}
                  aria-label="Close panel"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              )}
            </div>
          )}
          <div
            ref={contentRef}
            className={cn("flex-1 overflow-y-auto", contentClassName ?? "p-4 sm:p-6")}
            style={{ maxHeight: "100%" }}
          >
            {children}
          </div>
        </div>
      </>
    );

    return portal && typeof document !== "undefined"
      ? createPortal(content, document.body)
      : content;
  },
);
FloatingPanel.displayName = "FloatingPanel";

const FloatingPanelHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      "pb-4 border-b",
      className,
    )}
    {...props}
  />
);
FloatingPanelHeader.displayName = "FloatingPanelHeader";

const FloatingPanelFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      "pt-4 border-t",
      className,
    )}
    {...props}
  />
);
FloatingPanelFooter.displayName = "FloatingPanelFooter";

const FloatingPanelTitle = React.forwardRef<
  React.ElementRef<"h2">,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
FloatingPanelTitle.displayName = "FloatingPanelTitle";

const FloatingPanelDescription = React.forwardRef<
  React.ElementRef<"p">,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
FloatingPanelDescription.displayName = "FloatingPanelDescription";

export {
  FloatingPanel,
  FloatingPanelHeader,
  FloatingPanelFooter,
  FloatingPanelTitle,
  FloatingPanelDescription,
  type FloatingPanelProps,
  floatingPanelVariants,
};